// =============================================================================
// 15102026-2 -- RECEPTI BIDDER ROSTER + CMP WATCHDOG + PREBID 11.9.0
//               + FIRST-REQUEST TIMING TELEMETRY
// =============================================================================
// Base: 15102026/ads.js (the live 21102024 wrapper, prettier-formatted, with
// Mediapool removed). Changes in this build, all checked against the
// "Ringier Serbia - Header Bidding IDs" sheet, tab SuperRecepti.rs:
//
//   1. P5 Adform mid corrected from 1705630 (the In_Text_1 placement, sent by
//      mistake) to 1753064, the P5 placement in the sheet.
//   2. Criteo removed from all 8 ad units (sheet column "Criteo - Deleted"),
//      and its { name: "criteo" } user id removed from userSync.userIds.
//   3. AppNexus (Xandr) and SMN removed from all 8 ad units: neither works
//      with Ringier any more.
//   4. ConnectAd (networkId 44, siteId 1084677), PubMatic (publisherId
//      158892 + per-unit adSlot) and Rich Audience (per-unit pid,
//      supplyType "site") added to all 8 ad units with the sheet ids.
//   5. TCF/GPP CMP watchdog added at the top of this file (see its own
//      header below): if the consentmanager CMP is stuck with an empty
//      configuration, non-EEA visitors get a synthetic gdprApplies=false
//      answer so ads keep serving; a visitor whose CMP is progressing, and
//      any device on an EEA/CH/UK timezone, is always left to the real CMP.
//   6. Ships with a new Prebid library, prebid11.9.0.js in this folder
//      (Prebid 11.9.0; adapters: adf (provides the "adform" alias),
//      connectad, eskimi, ix, openx, pubmatic, richaudience, rtbhouse,
//      rubicon, smartadserver, teads). appnexus, admixer (the "smn" alias)
//      and criteo adapters plus criteoIdSystem are no longer compiled.
//   7. Passive first-request timing telemetry (see the "FIRST-REQUEST TIMING
//      TELEMETRY" block below). Two page-level GPT key-values, set right
//      before the first googletag.pubads().refresh() call of the page and
//      therefore carried by every ad request of the page view:
//
//        aq_first_request  -- milliseconds from the page's time origin
//                             (navigation start) to that first refresh()
//                             call, bucketed: lt_500, 500_999, 1000_1499,
//                             1500_1999, 2000_2999, 3000_4999, 5000_plus,
//                             unknown. Values are bare (no arm prefix).
//
//        aq_consent_wait   -- milliseconds from the time origin to the moment
//                             consent became FINAL for this page (real TCF
//                             "tcloaded" / "useractioncomplete", a
//                             gdprApplies=false answer, the CMP watchdog's
//                             synthetic answer, or "no CMP on the page"),
//                             same buckets; "pending" when the first ad
//                             request was fired before consent was final.
//                             When consent becomes final later, the
//                             page-level value is upgraded so the page's later
//                             requests (lazy slots, refreshes) carry the real
//                             number.
//
//      Out-of-page / interstitial slots never take the aq_first_request
//      stamp: the stamp must mean "the first in-page ad request", and an
//      interstitial (including GAM's codeless one, which GPT creates with an
//      auto-generated element id such as gpt_unit_/<network>/<site>/
//      Interstitial_0) has its own timing.
//      The CMP watchdog only takes notes for this (consentFinalAtMs /
//      consentFinalReason on window.__cmpWatchdog); its behaviour is
//      unchanged.
//   8. Page variant reader (debug only). The template assigns the A/B
//      "variant" key ('a' or 'b') per page view in an inline script. The
//      wrapper can now read it (GPT page targeting first, inline script text
//      as the fallback) and exposes it as window.__adsTelemetry.variant.
//      Nothing in this build behaves differently by variant.
//
//      Debugging: window.__adsTelemetry = { variant, variantSource,
//      readVariant(), snapshot() }; ?addebug=1 logs the telemetry steps.
//
// Everything else (slots, sizes, timeouts, refresh, lazy loading, targeting,
// consentManagement settings) is unchanged from 15102026.
// =============================================================================

// =============================================================================
// TCF CMP WATCHDOG
// =============================================================================
//
// Background: a consentmanager.net CMP is embedded in this site's templates.
// When its remote configuration fails to load, the CMP answers every TCF query
// with cmpStatus "loading" forever: no banner is rendered, no consent string
// is ever produced, and gdprApplies stays true. GPT and Prebid are both
// consent-aware, so in that state neither of them sends a single ad request
// and every slot on the site goes dark. This happened for real on 03.09.2026
// and cost a full ad outage.
//
// What the failure actually looks like (measured 04.09.2026): the CMP core
// script loads and its first configuration call (/delivery/cmp.php) completes
// normally, but the answer carries no configuration -- the account's designs
// are all geo-targeted and none is marked as fallback, so a visitor whose
// country matches no design gets nothing. On a healthy load the design file
// (/delivery/customdata/...) is requested within a few milliseconds of
// cmp.php finishing; on the broken load nothing ever follows cmp.php.
//
// What this watchdog does:
//
//   1. It wraps window.__tcfapi in a transparent pass-through proxy. While
//      the CMP behaves normally, the proxy changes nothing: every call and
//      every event goes straight through, and consent behavior is entirely
//      the CMP's.
//
//   2. It watches the CMP's own network activity (the design-file <script>
//      element in the DOM, and the browser's resource timeline). If cmp.php
//      has completed and no design file has been requested for
//      CMP_NO_DESIGN_CONFIRM_MILLISECONDS afterwards, the
//      CMP is in the broken state described above and the watchdog takes
//      over. This is a positive detection of the failure, not a timeout, so
//      it fires early (about 2 seconds into the page) for a broken visitor and
//      NEVER fires for a visitor whose CMP is merely slow but progressing.
//
//   3. A visitor for whom the CMP IS progressing (design file requested, or
//      the CMP's own status advanced, or a real "cmpuishown" / "tcloaded"
//      event arrived) is left entirely to the real CMP. That includes EU
//      visitors, who must see the banner and whose ads must wait for their
//      choice. The watchdog stands down for good on any such signal.
//
//   4. As a last resort, if the CMP shows no progress at all for
//      CMP_WATCHDOG_MAX_WAIT_MILLISECONDS (its scripts never loaded, the
//      vendor is entirely unreachable), the watchdog also takes over, so a
//      genuine vendor outage still cannot black out the site for the
//      non-EEA audience.
//
//   5. Before taking over it checks the visitor's device timezone. A device
//      set to an unambiguously EEA/CH/UK timezone (Berlin, Vienna, Zurich,
//      Paris, ...) is never answered synthetically: for that visitor the
//      watchdog does nothing, and if the CMP cannot serve them they get no
//      ads rather than ads without consent. Timezones that Serbian devices
//      commonly report (the Windows "Belgrade, Bratislava, Budapest,
//      Ljubljana, Prague" group maps to Europe/Budapest on many machines)
//      are deliberately NOT on that list, so a Serbian visitor is never
//      denied ads by this check. It is a fail-safe, not a geo lookup.
//
//   6. On takeover, every TCF consumer that registered through the proxy
//      (GPT, Prebid) immediately receives a synthetic final answer with
//      gdprApplies = false, and every later TCF call is answered the same
//      way. gdprApplies = false is the correct resolution for this site's
//      overwhelmingly Serbian (non-EEA) audience and is exactly how the
//      working consentmanager instances on this network resolve for them.
//
//   7. It is self-healing in both directions. If the account is fixed, the
//      design file arrives on every load and the watchdog never fires. If the
//      real CMP asserts itself after a takeover (a genuine "cmpuishown" or
//      final event), the proxy returns to pass-through, the synthetic answers
//      stop, and the GPT recovery refresh is never issued.
//
// Known limitation, accepted: a consumer that registered its listener
// directly with the broken CMP BEFORE this wrapper executed cannot be
// notified through the proxy; the consentmanager registry rescue below
// covers that case for this vendor specifically.
//
// Debugging: window.__cmpWatchdog exposes
// { wrapped, takenOver, cmpFinal, cmpHealthy, takeoverReason, timezone, timezoneBlocked,
//   consentFinalAtMs, consentFinalReason }.
// The last two are passive telemetry notes (when, on the performance.now()
// clock, consent stopped blocking ad requests, and why); nothing in the
// watchdog reads them back.

// After the CMP's configuration call (cmp.php) has completed, how long to wait
// for the design file request before concluding the configuration was empty.
// On a healthy load the design file follows within ~10 ms; 750 ms is a very
// wide margin.
var CMP_NO_DESIGN_CONFIRM_MILLISECONDS = 750;

// Absolute ceiling: if the CMP has shown no progress of any kind for this
// long after the proxy was installed, take over anyway (vendor unreachable).
// Generous on purpose: an EU visitor on a slow connection may need several
// seconds just for cmp.php (measured 2.6 s from Germany), and taking over
// before that would bypass their banner. Serbian visitors on the broken
// instance are not affected by this value at all -- their takeover comes from
// the empty-configuration signature at ~2 s.
var CMP_WATCHDOG_MAX_WAIT_MILLISECONDS = 8000;

// How often the watchdog re-evaluates the CMP's progress.
var CMP_WATCHDOG_POLL_MILLISECONDS = 250;

// Device timezones that identify an EEA/CH/UK visitor beyond reasonable doubt.
// The synthetic "GDPR does not apply" answer is never given to these. The
// list intentionally omits every zone a Serbian device might report:
// Europe/Belgrade itself, and the Windows group-default zones Europe/Budapest
// (Belgrade/Bratislava/Budapest/Ljubljana/Prague group) and Europe/Warsaw
// (Sarajevo/Skopje/Warsaw/Zagreb group), plus Prague, Bratislava, Ljubljana
// and Zagreb which share those groups.
var CMP_WATCHDOG_EEA_DEVICE_TIMEZONES = [
    'Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'Europe/Amsterdam',
    'Europe/Brussels', 'Europe/Luxembourg', 'Europe/Paris', 'Europe/Madrid',
    'Europe/Rome', 'Europe/Malta', 'Europe/Copenhagen', 'Europe/Stockholm',
    'Europe/Oslo', 'Europe/Helsinki', 'Europe/Athens', 'Europe/Bucharest',
    'Europe/Sofia', 'Europe/Vilnius', 'Europe/Riga', 'Europe/Tallinn',
    'Europe/Dublin', 'Europe/London', 'Europe/Lisbon', 'Atlantic/Reykjavik',
    'Atlantic/Madeira', 'Atlantic/Azores', 'Atlantic/Canary', 'Europe/Nicosia',
    'Asia/Nicosia', 'Europe/Vaduz'
];

// The CMP stub is normally installed by the template before this file runs,
// but load order is not guaranteed. If window.__tcfapi is not there yet, the
// watchdog polls for it this often, for at most this long, then gives up
// (no CMP on the page means nothing blocks ad serving and there is nothing
// to guard).
var CMP_APPEARANCE_POLL_MILLISECONDS = 200;
var CMP_APPEARANCE_MAX_WAIT_MILLISECONDS = 10000;

(function installCmpWatchdog() {

    // Read-only state for debugging from the console / remote sessions.
    var watchdogState = {
        wrapped: false,
        takenOver: false,
        cmpFinal: false,
        cmpHealthy: false,
        takeoverReason: null,
        timezone: null,
        timezoneBlocked: false,
        // Telemetry notes for aq_consent_wait (see recordConsentFinal).
        consentFinalAtMs: null,
        consentFinalReason: null
    };
    window.__cmpWatchdog = watchdogState;

    // Records the first moment consent stopped blocking ad requests, in
    // milliseconds since the page's time origin. Reasons: cmp_tcloaded,
    // cmp_useractioncomplete, gdpr_not_applicable, watchdog, no_cmp.
    // Passive: the first call wins and nothing in the watchdog reads these
    // two fields back, so the watchdog's decisions are unaffected.
    function recordConsentFinal(reason) {
        try {
            if (watchdogState.consentFinalAtMs !== null) {
                return;
            }
            var consentFinalMilliseconds = 0;
            if (window.performance && typeof window.performance.now === 'function') {
                consentFinalMilliseconds = window.performance.now();
            }
            watchdogState.consentFinalAtMs = consentFinalMilliseconds;
            watchdogState.consentFinalReason = reason;
        } catch (telemetryError) {
            // Telemetry must never break the watchdog.
        }
    }

    // The CMP's own __tcfapi function once the proxy is installed.
    var realTcfApi = null;

    // Every listener registered through the proxy, so the watchdog can answer
    // them itself on takeover. Each entry is { callback, syntheticListenerId }.
    var proxiedListeners = [];

    // Synthetic listener ids start high so they can never collide with ids
    // handed out by the real CMP.
    var nextSyntheticListenerId = 100000;

    // The progress poll timer, and when the proxy was installed (poll clock).
    var progressPollTimer = null;
    var proxyInstalledAtMilliseconds = 0;

    function logWatchdog(message) {
        try {
            console.log('[ads.js CMP watchdog] ' + message);
        } catch (ignored) {}
    }

    function nowMilliseconds() {
        return new Date().getTime();
    }

    // True while the watchdog's synthetic answers are in force: it has taken
    // over AND the real CMP has neither finished nor asserted itself since.
    function syntheticAnswersActive() {
        return watchdogState.takenOver && !watchdogState.cmpFinal && !watchdogState.cmpHealthy;
    }

    // -------------------------------------------------------------------------
    // Device timezone fail-safe (see point 5 in the header comment).
    // -------------------------------------------------------------------------
    function readDeviceTimezone() {
        try {
            var intlObject = window.Intl || (typeof Intl !== 'undefined' ? Intl : null);
            if (!intlObject || typeof intlObject.DateTimeFormat !== 'function') {
                return null;
            }
            var resolved = intlObject.DateTimeFormat().resolvedOptions();
            if (resolved && typeof resolved.timeZone === 'string' && resolved.timeZone.length > 0) {
                return resolved.timeZone;
            }
        } catch (timezoneError) {
            return null;
        }
        return null;
    }

    function deviceLooksEea() {
        var timezone = readDeviceTimezone();
        watchdogState.timezone = timezone;
        if (timezone === null) {
            // Unknown timezone (very old browser): cannot tell, do not block.
            return false;
        }
        var zoneIndex;
        for (zoneIndex = 0; zoneIndex < CMP_WATCHDOG_EEA_DEVICE_TIMEZONES.length; zoneIndex++) {
            if (CMP_WATCHDOG_EEA_DEVICE_TIMEZONES[zoneIndex] === timezone) {
                return true;
            }
        }
        return false;
    }

    // -------------------------------------------------------------------------
    // CMP progress detection through the resource timeline and the CMP's own
    // public state (see points 2-4 in the header comment).
    // -------------------------------------------------------------------------

    // Returns { configCallCompletedAt: ms-since-page-start or null,
    //           designFileRequested: bool }.
    //
    // The design file is requested by the CMP core as an async <script>
    // element appended to the document the moment cmp.php has executed
    // (measured: 2 ms later). Its download can take several seconds on a slow
    // connection, and a resource-timeline entry only exists once a download
    // has COMPLETED -- so an in-flight design file is invisible in the
    // timeline. The DOM is therefore checked first: a <script> whose src is a
    // consentmanager /delivery/customdata/ URL means the configuration
    // arrived and a design is being fetched, i.e. the CMP is working for this
    // visitor, whether or not the file has finished downloading yet.
    function inspectCmpNetworkActivity() {
        var result = { configCallCompletedAt: null, designFileRequested: false };
        try {
            var scriptElements = document.getElementsByTagName('script');
            var scriptIndex;
            for (scriptIndex = 0; scriptIndex < scriptElements.length; scriptIndex++) {
                var scriptSource = String(scriptElements[scriptIndex].src || '');
                if (scriptSource.indexOf('consentmanager') !== -1 && scriptSource.indexOf('/delivery/customdata/') !== -1) {
                    result.designFileRequested = true;
                    break;
                }
            }
        } catch (domError) {
            // fall through to the timeline check
        }
        try {
            if (!window.performance || typeof window.performance.getEntriesByType !== 'function') {
                return result;
            }
            var resourceEntries = window.performance.getEntriesByType('resource');
            var entryIndex;
            for (entryIndex = 0; entryIndex < resourceEntries.length; entryIndex++) {
                var resourceName = String(resourceEntries[entryIndex].name);
                if (resourceName.indexOf('consentmanager') === -1) {
                    continue;
                }
                if (resourceName.indexOf('/delivery/cmp.php') !== -1) {
                    // responseEnd is exposed for cross-origin resources even
                    // without Timing-Allow-Origin; a still-in-flight request
                    // does not appear in the timeline at all.
                    var responseEnd = resourceEntries[entryIndex].responseEnd;
                    if (typeof responseEnd === 'number' && responseEnd > 0) {
                        if (result.configCallCompletedAt === null || responseEnd < result.configCallCompletedAt) {
                            result.configCallCompletedAt = responseEnd;
                        }
                    }
                }
                if (resourceName.indexOf('/delivery/customdata/') !== -1 || resourceName.indexOf('/delivery/info/') !== -1) {
                    result.designFileRequested = true;
                }
            }
        } catch (performanceError) {
            return result;
        }
        return result;
    }

    // The consentmanager core exposes window.cmpmngr.status: it stays at 1 in
    // the broken state and advances (4 = banner shown, 6 = finished) once the
    // configuration has arrived. Any advance beyond 1 is progress.
    function cmpVendorStatusAdvanced() {
        try {
            var cmpVendorObject = window.cmpmngr;
            if (cmpVendorObject && typeof cmpVendorObject.status === 'number' && cmpVendorObject.status > 1) {
                return true;
            }
        } catch (statusError) {
            return false;
        }
        return false;
    }

    // Milliseconds since the page's time origin, on the same clock as the
    // resource timeline entries.
    function millisecondsSincePageStart() {
        try {
            if (window.performance && typeof window.performance.now === 'function') {
                return window.performance.now();
            }
        } catch (performanceError) {
            return 0;
        }
        return 0;
    }

    // Marks the CMP as genuinely working for this visitor and stands the
    // watchdog down permanently. Called on any progress signal.
    function markCmpHealthy(reason) {
        if (watchdogState.cmpHealthy) {
            return;
        }
        watchdogState.cmpHealthy = true;
        stopProgressPoll();
        if (watchdogState.takenOver) {
            logWatchdog('real CMP asserted itself after a takeover (' + reason + '); synthetic answers withdrawn, proxy back to pass-through.');
        } else {
            logWatchdog('CMP is progressing on its own (' + reason + '); watchdog stood down.');
        }
    }

    function stopProgressPoll() {
        if (progressPollTimer !== null) {
            clearInterval(progressPollTimer);
            progressPollTimer = null;
        }
    }

    // One evaluation of the CMP's progress; runs every
    // CMP_WATCHDOG_POLL_MILLISECONDS until a decision is reached.
    function evaluateCmpProgress() {
        if (watchdogState.cmpFinal || watchdogState.cmpHealthy || watchdogState.takenOver) {
            stopProgressPoll();
            return;
        }

        if (cmpVendorStatusAdvanced()) {
            markCmpHealthy('cmpmngr.status advanced past 1');
            return;
        }

        var networkActivity = inspectCmpNetworkActivity();
        if (networkActivity.designFileRequested) {
            markCmpHealthy('design file requested');
            return;
        }

        var elapsedSinceInstall = nowMilliseconds() - proxyInstalledAtMilliseconds;

        if (networkActivity.configCallCompletedAt !== null) {
            var sinceConfigCall = millisecondsSincePageStart() - networkActivity.configCallCompletedAt;
            if (sinceConfigCall >= CMP_NO_DESIGN_CONFIRM_MILLISECONDS) {
                takeOver('cmp.php completed ' + Math.round(sinceConfigCall) + 'ms ago and no design file followed (empty configuration for this visitor)');
                return;
            }
        }

        if (elapsedSinceInstall >= CMP_WATCHDOG_MAX_WAIT_MILLISECONDS) {
            takeOver('no CMP progress of any kind within ' + CMP_WATCHDOG_MAX_WAIT_MILLISECONDS + 'ms (vendor unreachable)');
            return;
        }
    }

    // A minimal but complete TCData object representing "GDPR does not apply".
    // Consumers only read gdprApplies/eventStatus/cmpStatus in this case, but
    // the usual containers are present so nothing trips over a missing field.
    function buildSyntheticTcData() {
        return {
            // Marker so the watchdog's own observers can tell a synthetic
            // answer from a genuine CMP recovery; harmless extra field for
            // every other TCF consumer.
            syntheticWatchdogAnswer: true,
            tcString: '',
            tcfPolicyVersion: 4,
            cmpId: 31,
            cmpVersion: 1,
            gdprApplies: false,
            eventStatus: 'tcloaded',
            cmpStatus: 'loaded',
            isServiceSpecific: true,
            useNonStandardTexts: false,
            publisherCC: 'RS',
            purposeOneTreatment: false,
            listenerId: null,
            purpose: { consents: {}, legitimateInterests: {} },
            vendor: { consents: {}, legitimateInterests: {} },
            specialFeatureOptins: {},
            publisher: {
                consents: {},
                legitimateInterests: {},
                customPurpose: { consents: {}, legitimateInterests: {} },
                restrictions: {}
            }
        };
    }

    function answerListenerSynthetically(listenerEntry) {
        var syntheticData = buildSyntheticTcData();
        syntheticData.listenerId = listenerEntry.syntheticListenerId;
        try {
            listenerEntry.callback(syntheticData, true);
        } catch (listenerError) {
            logWatchdog('a TCF listener threw while receiving the synthetic answer: ' + listenerError);
        }
    }

    // After a takeover, the proxy can only answer consumers that registered
    // THROUGH it. A consumer that registered before this file executed (GPT's
    // pubads implementation wins that race on some page loads) left its
    // callback inside the real CMP's own registry, forever waiting -- the
    // proxy cannot reach it. For the consentmanager CMP specifically, that
    // registry is publicly reachable at window.cmpmngr.api.events as entries
    // of shape { type: 'tcfv2', listener: function, id: number }, so the
    // watchdog answers those held listeners directly as well. Every step is
    // guarded: any unexpected shape (different CMP vendor, changed internals
    // after a consentmanager update) simply skips the rescue and the proxy
    // path still covers consumers that registered normally. A consumer that
    // registered through the proxy also appears in this registry and will
    // receive the synthetic answer twice; TCF listeners are event streams and
    // both GPT and Prebid handle a repeated final event idempotently.
    // Registry entries already answered, keyed by the CMP's own listener id, so
    // retries never answer the same consumer twice.
    var rescuedRegistryIds = {};

    function rescueCmpHeldListeners() {
        var rescuedCount = 0;
        try {
            var cmpVendorObject = window.cmpmngr;
            if (!cmpVendorObject || !cmpVendorObject.api || !cmpVendorObject.api.events || typeof cmpVendorObject.api.events.forEach !== 'function') {
                return 0;
            }
            cmpVendorObject.api.events.forEach(function (registryEntry) {
                if (!registryEntry || registryEntry.type !== 'tcfv2' || typeof registryEntry.listener !== 'function') {
                    return;
                }
                if (rescuedRegistryIds[registryEntry.id] === true) {
                    return;
                }
                rescuedRegistryIds[registryEntry.id] = true;
                var syntheticData = buildSyntheticTcData();
                syntheticData.listenerId = registryEntry.id;
                try {
                    registryEntry.listener(syntheticData, true);
                    rescuedCount = rescuedCount + 1;
                } catch (listenerError) {
                    logWatchdog('a CMP-held TCF listener threw while receiving the synthetic answer: ' + listenerError);
                }
            });
        } catch (rescueError) {
            logWatchdog('CMP-held listener rescue skipped: ' + rescueError);
        }
        return rescuedCount;
    }

    // The registry (window.cmpmngr) belongs to the CMP's full code, which loads
    // over the network and can appear AFTER the takeover fired -- and consumers
    // held in the CMP's stub queue only land in the registry when that code
    // processes the queue. A single rescue pass at takeover therefore misses
    // late arrivals (whether GPT is freed then depends purely on load
    // timing). So the rescue
    // repeats every second for a while after takeover, answering each newly
    // appeared captive listener exactly once, and stops early if the CMP
    // genuinely recovers or asserts itself.
    var CMP_RESCUE_RETRY_INTERVAL_MILLISECONDS = 1000;
    var CMP_RESCUE_MAX_DURATION_MILLISECONDS = 30000;


    // Ad requests that GPT attempted WHILE it was still consent-blocked are
    // dropped, not queued -- freeing GPT afterwards does not resend them
    // (a page whose request timing lands before the rescue stays blank with
    // zero gampad requests). So after the first rescue pass that actually
    // freed listeners, wait a moment and check whether ANY ad request has left
    // the page; if none has, issue a single recovery refresh of all GPT slots.
    // The completed Prebid auctions' targeting is already set on the slots, so
    // the recovery request serves with full header-bidding competition.
    // Never issued once the real CMP has asserted itself: for that visitor
    // ad requests must wait for the real consent outcome.
    var gptRedispatchScheduled = false;

    function scheduleGptRedispatchIfSilent() {
        if (gptRedispatchScheduled) {
            return;
        }
        gptRedispatchScheduled = true;
        setTimeout(function () {
            try {
                if (!syntheticAnswersActive()) {
                    logWatchdog('real CMP is in charge; recovery refresh not issued.');
                    return;
                }
                var observedAdRequestCount = 0;
                try {
                    var resourceEntries = [];
                    if (window.performance && typeof window.performance.getEntriesByType === 'function') {
                        resourceEntries = window.performance.getEntriesByType('resource');
                    }
                    var entryIndex;
                    for (entryIndex = 0; entryIndex < resourceEntries.length; entryIndex++) {
                        if (String(resourceEntries[entryIndex].name).indexOf('gampad/ads') !== -1) {
                            observedAdRequestCount = observedAdRequestCount + 1;
                        }
                    }
                } catch (performanceError) {
                    observedAdRequestCount = 0;
                }
                if (observedAdRequestCount > 0) {
                    logWatchdog('GPT already dispatched ' + observedAdRequestCount + ' ad request(s); no recovery refresh needed.');
                    return;
                }
                if (window.googletag && window.googletag.cmd) {
                    window.googletag.cmd.push(function () {
                        // Recover only the slots that were meant to load with the
                        // page. A lazy-loaded slot (div class "lazyAd") has not been
                        // requested yet and will be requested by the lazy-load
                        // observer when it scrolls into range; refreshing it here
                        // would send an out-of-view request without Prebid
                        // targeting and then a second request later. On this
                        // site P2-P5 carry the lazyAd class.
                        var slotsToRecover = [];
                        var managedSlots = window.googletag.pubads().getSlots();
                        var slotIndex;
                        for (slotIndex = 0; slotIndex < managedSlots.length; slotIndex++) {
                            var managedSlot = managedSlots[slotIndex];
                            var slotElement = null;
                            try {
                                slotElement = document.getElementById(managedSlot.getSlotElementId());
                            } catch (lookupError) {
                                slotElement = null;
                            }
                            var isLazyLoaded = false;
                            if (slotElement && slotElement.classList && typeof slotElement.classList.contains === 'function') {
                                isLazyLoaded = slotElement.classList.contains('lazyAd');
                            }
                            if (!isLazyLoaded) {
                                slotsToRecover.push(managedSlot);
                            }
                        }
                        if (slotsToRecover.length > 0) {
                            window.googletag.pubads().refresh(slotsToRecover);
                        }
                    });
                    logWatchdog('GPT sent no ad requests while consent was blocked; issued a one-time recovery refresh of the non-lazy slots.');
                }
            } catch (redispatchError) {
                logWatchdog('GPT recovery refresh skipped: ' + redispatchError);
            }
        }, 1500);
    }

    function startCmpRegistryRescueLoop() {
        var firstPassCount = rescueCmpHeldListeners() + rescueGppRegistryListeners();
        if (firstPassCount > 0) {
            logWatchdog('rescued ' + firstPassCount + ' listener(s) held inside the CMP registry (consumers that registered before this file executed).');
            scheduleGptRedispatchIfSilent();
        }
        var elapsedMilliseconds = 0;
        var rescueTimer = setInterval(function () {
            if (!syntheticAnswersActive()) {
                clearInterval(rescueTimer);
                return;
            }
            var passCount = rescueCmpHeldListeners() + rescueGppRegistryListeners();
            if (passCount > 0) {
                logWatchdog('rescued ' + passCount + ' more CMP-held listener(s) on a retry pass.');
                scheduleGptRedispatchIfSilent();
            }
            elapsedMilliseconds = elapsedMilliseconds + CMP_RESCUE_RETRY_INTERVAL_MILLISECONDS;
            if (elapsedMilliseconds >= CMP_RESCUE_MAX_DURATION_MILLISECONDS) {
                clearInterval(rescueTimer);
            }
        }, CMP_RESCUE_RETRY_INTERVAL_MILLISECONDS);
    }

    // Marks the CMP as finished and stands the watchdog down. Called whenever a
    // real final event is observed through the proxy.
    function markCmpFinal() {
        if (watchdogState.cmpFinal) {
            return;
        }
        watchdogState.cmpFinal = true;
        stopProgressPoll();
        logWatchdog('CMP reached a final state on its own; watchdog stood down.');
    }

    function takeOver(reason) {
        if (watchdogState.takenOver || watchdogState.cmpFinal || watchdogState.cmpHealthy) {
            return;
        }
        stopProgressPoll();

        // Fail-safe: never bypass consent for a device that is clearly in the
        // EEA. For that visitor the watchdog does nothing at all.
        if (deviceLooksEea()) {
            watchdogState.timezoneBlocked = true;
            logWatchdog('CMP looks broken (' + reason + ') but the device timezone is ' + watchdogState.timezone +
                ' (EEA); leaving consent to the real CMP, no synthetic answer for this visitor.');
            return;
        }

        watchdogState.takenOver = true;
        watchdogState.takeoverReason = reason;
        // From this moment every consumer gets gdprApplies=false, so ad
        // requests are no longer blocked by consent (telemetry note).
        recordConsentFinal('watchdog');
        logWatchdog(
            'taking over: ' + reason + '; answering ' + proxiedListeners.length +
            ' registered TCF listener(s) with gdprApplies=false so ad serving can proceed.');
        var listenerIndex;
        for (listenerIndex = 0; listenerIndex < proxiedListeners.length; listenerIndex++) {
            answerListenerSynthetically(proxiedListeners[listenerIndex]);
        }
        takeOverGpp();
        startCmpRegistryRescueLoop();
    }

    // Watches a real CMP event. Final states per the TCF spec are "tcloaded"
    // (no UI needed) and "useractioncomplete" (user answered the banner).
    // "cmpuishown" is not final but proves the CMP is configured and working
    // for this visitor, so it counts as the CMP asserting itself.
    function observeRealEvent(tcData) {
        // A synthetic answer produced by this watchdog (delivered back to us
        // through the CMP registry rescue) must never count as the CMP
        // recovering -- only genuine CMP events may stand the watchdog down.
        if (tcData && tcData.syntheticWatchdogAnswer) {
            return;
        }
        if (!tcData) {
            return;
        }
        // Telemetry notes only (aq_consent_wait); the watchdog's own decisions
        // below are unchanged. A real gdprApplies=false answer, or a final TCF
        // event, is the moment the consent-aware ad systems stop waiting.
        if (tcData.gdprApplies === false) {
            recordConsentFinal('gdpr_not_applicable');
        }
        if (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete') {
            recordConsentFinal('cmp_' + tcData.eventStatus);
        }
        if (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete') {
            markCmpHealthy('real final event ' + tcData.eventStatus);
            markCmpFinal();
            return;
        }
        if (tcData.eventStatus === 'cmpuishown' || tcData.cmpStatus === 'loaded') {
            markCmpHealthy('real event ' + tcData.eventStatus + ' / cmpStatus ' + tcData.cmpStatus);
        }
    }



    // Consumers inside iframes -- and, on some pages, GPT itself -- talk to
    // the CMP through the TCF postMessage protocol aimed at the
    // __tcfapiLocator frame instead of calling window.__tcfapi directly.
    // Those requests never pass through the function proxy above, so after a
    // takeover the watchdog answers them here as well. When GPT binds itself
    // to the postMessage path it stays blocked even after the function-path
    // rescue -- __tcfapiCall messages get ZERO __tcfapiReturn replies from
    // the broken CMP. While the
    // CMP is healthy (or before a takeover) this responder stays silent, and
    // it stands down permanently if the real CMP asserts itself.
    function installPostMessageResponder() {
        window.addEventListener('message', function (messageEvent) {
            if (!syntheticAnswersActive()) {
                return;
            }
            var payload = messageEvent && messageEvent.data;
            var usedJsonEncoding = false;
            if (typeof payload === 'string') {
                if (payload.indexOf('__tcfapiCall') === -1) {
                    return;
                }
                try {
                    payload = JSON.parse(payload);
                    usedJsonEncoding = true;
                } catch (parseError) {
                    return;
                }
            }
            if (!payload || !payload.__tcfapiCall || !messageEvent.source) {
                return;
            }
            var tcfCall = payload.__tcfapiCall;
            var returnValue;
            if (tcfCall.command === 'ping') {
                returnValue = {
                    gdprApplies: false,
                    cmpLoaded: true,
                    cmpStatus: 'loaded',
                    displayStatus: 'disabled',
                    apiVersion: '2.2',
                    cmpId: 31
                };
            } else {
                returnValue = buildSyntheticTcData();
            }
            var reply = { __tcfapiReturn: { returnValue: returnValue, success: true, callId: tcfCall.callId } };
            try {
                messageEvent.source.postMessage(usedJsonEncoding ? JSON.stringify(reply) : reply, '*');
            } catch (postError) {
                return;
            }
            // A consumer reached through this path may also have had its ad
            // requests dropped while blocked; make sure the one-time recovery
            // refresh check runs for this path too.
            if (tcfCall.command === 'addEventListener' || tcfCall.command === 'getTCData') {
                scheduleGptRedispatchIfSilent();
            }
        }, false);
    }

    // =========================================================================
    // GPP (GLOBAL PRIVACY PLATFORM) WATCHDOG
    // =========================================================================
    //
    // The same broken CMP that blocks TCF also implements a second, independent
    // consent signal API: GPP (Global Privacy Platform, used for US state
    // privacy laws and other non-EU frameworks). It fails identically:
    // window.__gpp answers every query with cmpStatus "loading" and
    // signalStatus "not ready" forever. Google Publisher Tag consults GPP in
    // addition to TCF before it will request ads, so even after the TCF
    // watchdog above frees GPT, a stuck GPP signal can still block ad serving
    // on its own: with TCF fully resolved (every tcfv2 listener answered,
    // Prebid running complete auctions) zero ad requests fire until GPP is
    // also freed here.
    //
    // Mirrors the TCF watchdog's three surfaces -- the window.__gpp function,
    // the consentmanager registry (entries with type 'gpp'), and the
    // postMessage protocol via the __gppLocator frame -- and fires on the same
    // takeover as TCF. A healthy GPP resolves on its own and this code never
    // engages.

    var gppRealApi = null;
    var gppProxiedListeners = [];
    var gppNextSyntheticListenerId = 300000;
    var gppRescuedRegistryIds = {};

    // A resolved-and-nothing-applies GPP signal: no section of any privacy
    // framework currently applies, which is the correct declaration when the
    // audience is not covered by the regulations GPP encodes (this wrapper's
    // watchdog already declares the TCF/GDPR equivalent above for the same
    // non-EEA audience).
    function buildSyntheticGppPingData() {
        return {
            gppVersion: '1.1',
            cmpStatus: 'loaded',
            cmpDisplayStatus: 'hidden',
            signalStatus: 'ready',
            supportedAPIs: [],
            cmpId: 31,
            sectionList: [],
            applicableSections: [],
            gppString: '',
            parsedSections: {}
        };
    }

    // GPP's addEventListener callback receives an "event" object, not the ping
    // data directly. Different integrations read either event.data (spec says
    // a plain 'ready' string for the signalStatus event) or the ping-shaped
    // fields nested under event.pingData -- both are populated here, same
    // belt-and-braces approach as the TCF synthetic answer above.
    function buildSyntheticGppEvent(listenerId) {
        return {
            eventName: 'signalStatus',
            listenerId: listenerId,
            data: 'ready',
            pingData: buildSyntheticGppPingData()
        };
    }

    function answerGppListenerSynthetically(listenerEntry) {
        try {
            listenerEntry.callback(buildSyntheticGppEvent(listenerEntry.syntheticListenerId), true);
        } catch (listenerError) {
            logWatchdog('a GPP listener threw while receiving the synthetic answer: ' + listenerError);
        }
    }

    function takeOverGpp() {
        var listenerIndex;
        for (listenerIndex = 0; listenerIndex < gppProxiedListeners.length; listenerIndex++) {
            answerGppListenerSynthetically(gppProxiedListeners[listenerIndex]);
        }
        rescueGppRegistryListeners();
    }

    // Same shape as the TCF registry rescue, filtered to type 'gpp' entries;
    // deduped by registry id so a repeat retry pass never answers twice.
    function rescueGppRegistryListeners() {
        var rescuedCount = 0;
        try {
            var cmpVendorObject = window.cmpmngr;
            if (!cmpVendorObject || !cmpVendorObject.api || !cmpVendorObject.api.events || typeof cmpVendorObject.api.events.forEach !== 'function') {
                return 0;
            }
            cmpVendorObject.api.events.forEach(function (registryEntry) {
                if (!registryEntry || registryEntry.type !== 'gpp' || typeof registryEntry.listener !== 'function') {
                    return;
                }
                if (gppRescuedRegistryIds[registryEntry.id] === true) {
                    return;
                }
                gppRescuedRegistryIds[registryEntry.id] = true;
                try {
                    registryEntry.listener(buildSyntheticGppEvent(registryEntry.id), true);
                    rescuedCount = rescuedCount + 1;
                } catch (listenerError) {
                    logWatchdog('a CMP-held GPP listener threw while receiving the synthetic answer: ' + listenerError);
                }
            });
        } catch (rescueError) {
            logWatchdog('GPP registry rescue skipped: ' + rescueError);
        }
        return rescuedCount;
    }

    function installGppProxy() {
        if (typeof window.__gpp !== 'function') {
            return;
        }
        gppRealApi = window.__gpp;

        window.__gpp = function proxiedGpp(command, callback, parameter) {

            if (command === 'addEventListener' && typeof callback === 'function') {
                var listenerEntry = {
                    callback: callback,
                    syntheticListenerId: gppNextSyntheticListenerId
                };
                gppNextSyntheticListenerId = gppNextSyntheticListenerId + 1;
                gppProxiedListeners.push(listenerEntry);

                if (syntheticAnswersActive()) {
                    answerGppListenerSynthetically(listenerEntry);
                }

                gppRealApi(command, function (event, success) {
                    if (syntheticAnswersActive()) {
                        return;
                    }
                    callback(event, success);
                }, parameter);
                return;
            }

            if (command === 'removeEventListener') {
                var entryIndex;
                for (entryIndex = 0; entryIndex < gppProxiedListeners.length; entryIndex++) {
                    if (gppProxiedListeners[entryIndex].syntheticListenerId === parameter) {
                        gppProxiedListeners.splice(entryIndex, 1);
                        if (typeof callback === 'function') {
                            callback(true);
                        }
                        return;
                    }
                }
                gppRealApi(command, callback, parameter);
                return;
            }

            if (syntheticAnswersActive()) {
                if (typeof callback === 'function') {
                    callback(buildSyntheticGppPingData(), true);
                    return;
                }
                return;
            }

            gppRealApi(command, callback, parameter);
        };
    }

    // Consumers reaching GPP through postMessage (__gppCall / __gppReturn via
    // the __gppLocator frame), same reasoning as the TCF postMessage responder
    // above: some consumers, including GPT itself on some page loads, never
    // call window.__gpp directly.
    function installGppPostMessageResponder() {
        window.addEventListener('message', function (messageEvent) {
            if (!syntheticAnswersActive()) {
                return;
            }
            var payload = messageEvent && messageEvent.data;
            var usedJsonEncoding = false;
            if (typeof payload === 'string') {
                if (payload.indexOf('__gppCall') === -1) {
                    return;
                }
                try {
                    payload = JSON.parse(payload);
                    usedJsonEncoding = true;
                } catch (parseError) {
                    return;
                }
            }
            if (!payload || !payload.__gppCall || !messageEvent.source) {
                return;
            }
            var gppCallInfo = payload.__gppCall;
            var returnValue = gppCallInfo.command === 'addEventListener'
                ? buildSyntheticGppEvent(gppCallInfo.callId)
                : buildSyntheticGppPingData();
            var reply = { __gppReturn: { returnValue: returnValue, success: true, callId: gppCallInfo.callId } };
            try {
                messageEvent.source.postMessage(usedJsonEncoding ? JSON.stringify(reply) : reply, '*');
            } catch (postError) {
                return;
            }
            scheduleGptRedispatchIfSilent();
        }, false);
    }


    function installProxy() {
        realTcfApi = window.__tcfapi;
        watchdogState.wrapped = true;
        installPostMessageResponder();
        installGppProxy();
        installGppPostMessageResponder();

        window.__tcfapi = function proxiedTcfApi(command, version, callback, parameter) {

            // --- addEventListener -------------------------------------------
            // Record the consumer so the watchdog can answer it on takeover,
            // and ALWAYS forward the registration to the real CMP so that a
            // healthy (or later-asserting) CMP drives the consumer normally.
            if (command === 'addEventListener' && typeof callback === 'function') {
                var listenerEntry = {
                    callback: callback,
                    syntheticListenerId: nextSyntheticListenerId
                };
                nextSyntheticListenerId = nextSyntheticListenerId + 1;
                proxiedListeners.push(listenerEntry);

                if (syntheticAnswersActive()) {
                    // Late consumer after takeover: answer immediately so it
                    // never hangs, and still register with the real CMP below
                    // in case it asserts itself.
                    answerListenerSynthetically(listenerEntry);
                }

                realTcfApi(command, version, function (tcData, success) {
                    observeRealEvent(tcData);
                    // While synthetic answers are in force, suppress the CMP's
                    // eternal "loading" pings; only real progress is worth
                    // forwarding then (and observeRealEvent just lifted the
                    // suppression if this event was real progress).
                    if (syntheticAnswersActive()) {
                        return;
                    }
                    callback(tcData, success);
                }, parameter);
                return;
            }

            // --- removeEventListener ----------------------------------------
            // If the id is one of the watchdog's synthetic ids, confirm the
            // removal locally (the real CMP has never heard of that id).
            if (command === 'removeEventListener') {
                var entryIndex;
                for (entryIndex = 0; entryIndex < proxiedListeners.length; entryIndex++) {
                    if (proxiedListeners[entryIndex].syntheticListenerId === parameter) {
                        proxiedListeners.splice(entryIndex, 1);
                        if (typeof callback === 'function') {
                            callback(true);
                        }
                        return;
                    }
                }
                realTcfApi(command, version, callback, parameter);
                return;
            }

            // --- getTCData / ping / everything else -------------------------
            // While synthetic answers are in force, answer one-shot queries
            // synthetically (the real CMP would only ever say "loading"
            // again). Otherwise pure pass-through, with a peek at the answer
            // to spot progress or a final state.
            if (syntheticAnswersActive()) {
                if (command === 'ping' && typeof callback === 'function') {
                    callback({
                        gdprApplies: false,
                        cmpLoaded: true,
                        cmpStatus: 'loaded',
                        displayStatus: 'disabled',
                        apiVersion: '2.2',
                        cmpId: 31
                    }, true);
                    return;
                }
                if (typeof callback === 'function') {
                    callback(buildSyntheticTcData(), true);
                    return;
                }
                return;
            }

            realTcfApi(command, version, function (tcData, success) {
                observeRealEvent(tcData);
                if (typeof callback === 'function') {
                    callback(tcData, success);
                }
            }, parameter);
        };

        // The watchdog's own observer: register with the real CMP so progress
        // or a final state is noticed even if no other consumer ever registers.
        realTcfApi('addEventListener', 2, function (tcData, success) {
            if (success !== false) {
                observeRealEvent(tcData);
            }
        });

        proxyInstalledAtMilliseconds = nowMilliseconds();
        progressPollTimer = setInterval(evaluateCmpProgress, CMP_WATCHDOG_POLL_MILLISECONDS);
        logWatchdog('proxy installed; watching CMP progress (takeover ' + CMP_NO_DESIGN_CONFIRM_MILLISECONDS +
            'ms after an empty configuration answer, or after ' + CMP_WATCHDOG_MAX_WAIT_MILLISECONDS + 'ms without any progress).');
    }

    // The CMP stub normally exists before this file runs; if not, poll for a
    // while in case the template loads it late. No CMP at all means nothing
    // blocks ad serving, so the watchdog simply never engages.
    if (typeof window.__tcfapi === 'function') {
        installProxy();
    } else {
        var appearanceWaitedMilliseconds = 0;
        var appearancePollTimer = setInterval(function () {
            if (typeof window.__tcfapi === 'function') {
                clearInterval(appearancePollTimer);
                installProxy();
                return;
            }
            appearanceWaitedMilliseconds = appearanceWaitedMilliseconds + CMP_APPEARANCE_POLL_MILLISECONDS;
            if (appearanceWaitedMilliseconds >= CMP_APPEARANCE_MAX_WAIT_MILLISECONDS) {
                clearInterval(appearancePollTimer);
                logWatchdog('no __tcfapi appeared within ' + CMP_APPEARANCE_MAX_WAIT_MILLISECONDS + 'ms; nothing to guard.');
                // No CMP at all means consent never blocked anything; the
                // telemetry note is taken at the moment this is established.
                recordConsentFinal('no_cmp');
            }
        }, CMP_APPEARANCE_POLL_MILLISECONDS);
    }
})();

// Wrapper start: on the window "load" event, or 2 s after DOMContentLoaded,
// whichever comes first (unchanged logic). 15102026-2: written out in full so
// the start moment and its trigger can be noted for the first-request
// telemetry; the order of operations is identical to the previous build.
window.addEventListener("load", function (e) {
  if (window.prebidAuctionRun) {
    return;
  }
  window.prebidAuctionRun = true;
  clearTimeout(window.adsLoadTimer);
  recordFirstRequestMilestone("wrapperStartMs", "wrapperStartReason", "load");
  runWrapper();
});
document.addEventListener("DOMContentLoaded", function (e) {
  window.adsLoadTimer = setTimeout(function () {
    if (window.prebidAuctionRun) {
      return;
    }
    window.prebidAuctionRun = true;
    recordFirstRequestMilestone("wrapperStartMs", "wrapperStartReason", "DOMContentLoaded_plus_2s");
    runWrapper();
  }, 2e3);
});
var googletag = googletag || {};
googletag.cmd = googletag.cmd || [];
var pbjs = pbjs || {};
((pbjs.que = pbjs.que || []), (window.ID5EspConfig = { partnerId: 1355 }));

// =============================================================================
// FIRST-REQUEST TIMING TELEMETRY (15102026-2, passive)
// =============================================================================
//
// See item 7 of the header of this file for the purpose. Everything here only
// reads clocks and writes page-level GPT key-values; nothing here delays,
// skips or reorders an ad request, and nothing reads these values back for
// delivery. Every function is wrapped in try/catch so a missing browser API
// can never break ad serving. No network requests are made.

// Bucket edges in milliseconds, shared by both keys. The labels double as the
// exact value list that exists under each key in GAM, so they must not
// change: reports compare them across sites.
var FIRST_REQUEST_TIMING_BUCKETS = [
  { upTo: 500, label: "lt_500" },
  { upTo: 1000, label: "500_999" },
  { upTo: 1500, label: "1000_1499" },
  { upTo: 2000, label: "1500_1999" },
  { upTo: 3000, label: "2000_2999" },
  { upTo: 5000, label: "3000_4999" },
];
var FIRST_REQUEST_TIMING_TOP_BUCKET = "5000_plus";
var FIRST_REQUEST_TIMING_UNKNOWN = "unknown";
var CONSENT_WAIT_PENDING = "pending";

// GPT key names: network-wide predefined keys (custom dimension reporting).
var GPT_KEY_FIRST_REQUEST = "aq_first_request";
var GPT_KEY_CONSENT_WAIT = "aq_consent_wait";

// Arm marker prepended to the aq_first_request values. This build: '' (bare
// lt_500 ... 5000_plus). A future A/B arm B may set 'b_' (b_lt_500 ...
// b_5000_plus, both value sets exist on the GAM key). aq_consent_wait is
// always bare. Nothing else reads this constant.
var FIRST_REQUEST_ARM_PREFIX = "";

// How often to re-check whether consent became final AFTER the first ad
// request went out with aq_consent_wait=pending, and for how long.
var CONSENT_WAIT_POLL_MILLISECONDS = 250;
var CONSENT_WAIT_POLL_MAX_MILLISECONDS = 60000;

// Raw timings for the debug snapshot. All values are milliseconds since the
// page's time origin (performance.now() clock), null until recorded.
var firstRequestTiming = {
  scriptStartMs: null,
  wrapperStartMs: null,
  wrapperStartReason: null,
  firstRequestBidsMs: null,
  firstRefreshCallMs: null,
  firstRefreshCallSlots: null,
  skippedOutOfPageRefreshCalls: 0,
  consentFinalMs: null,
  consentFinalReason: null,
  consentWaitAtFirstRequest: null,
  firstRequestBucket: null,
  consentWaitBucket: null,
  consentWaitBucketUpdatedAtMs: null,
  pageVariant: null,
  pageVariantSource: null,
  slotRequestedMs: {},
  slotResponseReceivedMs: {},
  slotRenderEndedMs: {},
};

// Logs telemetry steps to the console only when the page URL carries
// ?addebug=1 (or &addebug=1); silent otherwise.
function adsTelemetryDebugLog() {
  try {
    var searchText = "";
    if (window.location && typeof window.location.search === "string") {
      searchText = window.location.search;
    }
    if (searchText.indexOf("addebug=1") === -1) {
      return;
    }
    var logArguments = Array.prototype.slice.call(arguments);
    logArguments.unshift("[ads.js telemetry]");
    console.log.apply(console, logArguments);
  } catch (error) {
    // Logging is optional.
  }
}

// Milliseconds since the page's time origin; null when the clock is missing.
function timingNowMilliseconds() {
  try {
    if (window.performance && typeof window.performance.now === "function") {
      return window.performance.now();
    }
  } catch (error) {
    return null;
  }
  return null;
}

// Maps a duration to its GAM bucket label.
function getFirstRequestTimingBucket(milliseconds) {
  if (milliseconds === null || milliseconds === undefined || isNaN(milliseconds)) {
    return FIRST_REQUEST_TIMING_UNKNOWN;
  }
  var bucketIndex;
  for (bucketIndex = 0; bucketIndex < FIRST_REQUEST_TIMING_BUCKETS.length; bucketIndex++) {
    if (milliseconds < FIRST_REQUEST_TIMING_BUCKETS[bucketIndex].upTo) {
      return FIRST_REQUEST_TIMING_BUCKETS[bucketIndex].label;
    }
  }
  return FIRST_REQUEST_TIMING_TOP_BUCKET;
}

// Reads the consent-final moment the CMP watchdog noted (see
// recordConsentFinal in the watchdog block). Returns null while consent is
// still pending.
function readConsentFinalFromWatchdog() {
  try {
    var watchdog = window.__cmpWatchdog;
    if (watchdog && typeof watchdog.consentFinalAtMs === "number") {
      firstRequestTiming.consentFinalMs = watchdog.consentFinalAtMs;
      firstRequestTiming.consentFinalReason = watchdog.consentFinalReason || null;
      return watchdog.consentFinalAtMs;
    }
  } catch (error) {
    return null;
  }
  return null;
}

// Records a named milestone once (first call wins).
function recordFirstRequestMilestone(fieldName, extraFieldName, extraValue) {
  try {
    if (firstRequestTiming[fieldName] !== null) {
      return;
    }
    firstRequestTiming[fieldName] = timingNowMilliseconds();
    if (extraFieldName) {
      firstRequestTiming[extraFieldName] = extraValue;
    }
    adsTelemetryDebugLog(fieldName, Math.round(firstRequestTiming[fieldName] || -1), extraValue || "");
  } catch (error) {
    // Telemetry must never throw into the wrapper.
  }
}

// True when a GPT slot must NOT take the aq_first_request stamp: an
// out-of-page slot, or any interstitial. GAM's codeless interstitial is
// created by GPT itself with an auto-generated element id that starts with
// "gpt_unit_" (for example gpt_unit_/161634325/recepti.zena.rs/Interstitial_0),
// so it is caught by the element id prefix as well as by its name. A missing
// slot (a div absent on this page) also counts as excluded, because it
// requests nothing.
function isSlotExcludedFromFirstRequestStamp(slot) {
  try {
    if (!slot) {
      return true;
    }
    if (typeof slot.getOutOfPage === "function") {
      if (slot.getOutOfPage() === true) {
        return true;
      }
    }
    var elementId = "";
    if (typeof slot.getSlotElementId === "function") {
      elementId = String(slot.getSlotElementId() || "");
    }
    var adUnitPathText = "";
    if (typeof slot.getAdUnitPath === "function") {
      adUnitPathText = String(slot.getAdUnitPath() || "");
    }
    if (elementId.indexOf("gpt_unit_") === 0) {
      return true;
    }
    if (/interstitial/i.test(elementId)) {
      return true;
    }
    if (/interstitial/i.test(adUnitPathText)) {
      return true;
    }
  } catch (error) {
    // When in doubt, do not exclude: the slot is an ordinary wrapper slot.
    return false;
  }
  return false;
}

// Human-readable list of the element ids of a refresh call, for the snapshot.
function describeRefreshSlots(slotList) {
  var descriptionParts = [];
  try {
    var slotIndex;
    for (slotIndex = 0; slotIndex < slotList.length; slotIndex++) {
      var slot = slotList[slotIndex];
      if (slot && typeof slot.getSlotElementId === "function") {
        descriptionParts.push(String(slot.getSlotElementId()));
      }
    }
  } catch (error) {
    // Ignore: description is debug-only.
  }
  return descriptionParts.join(",");
}

// Finds the GPT slot defined for an element id (used by the lazy-load path,
// which knows the element id only). Returns null when none is found.
function findGptSlotByElementId(elementId) {
  try {
    var allSlots = googletag.pubads().getSlots();
    var slotIndex;
    for (slotIndex = 0; slotIndex < allSlots.length; slotIndex++) {
      if (allSlots[slotIndex].getSlotElementId() === elementId) {
        return allSlots[slotIndex];
      }
    }
  } catch (error) {
    return null;
  }
  return null;
}

// Determines the page's A/B variant ('a' or 'b'), assigned per page view by
// the template. Debug only for now: nothing in this file behaves differently
// by variant. Returns { variant, source } with variant null when unknown.
//
//   1. GPT page-level targeting (googletag.pubads().getTargeting('variant')),
//      available once gpt.js has loaded and the template's own
//      googletag.cmd callback has run.
//   2. Fallback: the template's inline script text, which contains a literal
//      call of the form setTargeting('variant', 'a') (single or double quotes).
function readPageVariant() {
  var result = { variant: null, source: null };

  // 1. GPT page-level targeting.
  try {
    if (typeof googletag !== "undefined" && googletag && typeof googletag.pubads === "function") {
      var pubadsService = googletag.pubads();
      if (pubadsService && typeof pubadsService.getTargeting === "function") {
        var targetingValues = pubadsService.getTargeting("variant");
        if (targetingValues && targetingValues.length > 0 && targetingValues[0]) {
          result.variant = String(targetingValues[0]);
          result.source = "gpt_targeting";
          return result;
        }
      }
    }
  } catch (error) {
    // Fall through to the inline script scan.
  }

  // 2. Inline script text.
  try {
    var variantCallPattern = /setTargeting\(\s*["']variant["']\s*,\s*["']([^"']+)["']\s*\)/;
    var scriptElements = document.getElementsByTagName("script");
    var scriptIndex;
    for (scriptIndex = 0; scriptIndex < scriptElements.length; scriptIndex++) {
      var scriptElement = scriptElements[scriptIndex];
      // Only inline scripts carry the template's call; external files are skipped.
      if (scriptElement.src) {
        continue;
      }
      var scriptText = scriptElement.text || scriptElement.textContent || "";
      var variantMatch = variantCallPattern.exec(scriptText);
      if (variantMatch) {
        result.variant = variantMatch[1];
        result.source = "inline_script";
        return result;
      }
    }
  } catch (error) {
    // Unknown variant.
  }

  return result;
}

// Writes the consent-wait key-value at page level. Called at the first ad
// request and again if consent becomes final later (pending -> bucket).
function applyConsentWaitTargeting(bucketLabel) {
  try {
    googletag.pubads().setTargeting(GPT_KEY_CONSENT_WAIT, bucketLabel);
    firstRequestTiming.consentWaitBucket = bucketLabel;
    firstRequestTiming.consentWaitBucketUpdatedAtMs = timingNowMilliseconds();
    adsTelemetryDebugLog(GPT_KEY_CONSENT_WAIT + "=" + bucketLabel);
  } catch (error) {
    // Ignore: page-level targeting is optional telemetry.
  }
}

// Polls the watchdog notes after a 'pending' first request so the page's
// later ad requests carry the real consent-wait bucket.
function startConsentWaitResolutionPoll() {
  var waitedMilliseconds = 0;
  var pollTimer = setInterval(function () {
    try {
      waitedMilliseconds = waitedMilliseconds + CONSENT_WAIT_POLL_MILLISECONDS;
      var consentFinalMs = readConsentFinalFromWatchdog();
      if (consentFinalMs !== null) {
        clearInterval(pollTimer);
        applyConsentWaitTargeting(getFirstRequestTimingBucket(consentFinalMs));
        return;
      }
      if (waitedMilliseconds >= CONSENT_WAIT_POLL_MAX_MILLISECONDS) {
        clearInterval(pollTimer);
      }
    } catch (error) {
      clearInterval(pollTimer);
    }
  }, CONSENT_WAIT_POLL_MILLISECONDS);
}

// Called immediately before EVERY googletag.pubads().refresh() call of this
// wrapper (the initial ATF call, lazy loads, viewability refreshes), with the
// list of slots about to be refreshed. The first call that refreshes at least
// one in-page slot sets aq_first_request; calls that only refresh
// out-of-page / interstitial slots never stamp. Every later call only
// upgrades aq_consent_wait from 'pending'.
function recordFirstRequestTelemetryBeforeRefresh(slotList) {
  try {
    var consentFinalMs = readConsentFinalFromWatchdog();

    if (firstRequestTiming.firstRefreshCallMs === null) {
      // Does this call refresh at least one in-page slot?
      var hasInPageSlot = false;
      var slotIndex;
      if (slotList) {
        for (slotIndex = 0; slotIndex < slotList.length; slotIndex++) {
          if (!isSlotExcludedFromFirstRequestStamp(slotList[slotIndex])) {
            hasInPageSlot = true;
          }
        }
      }

      if (hasInPageSlot) {
        firstRequestTiming.firstRefreshCallMs = timingNowMilliseconds();
        firstRequestTiming.firstRefreshCallSlots = describeRefreshSlots(slotList);
        firstRequestTiming.firstRequestBucket = getFirstRequestTimingBucket(firstRequestTiming.firstRefreshCallMs);
        var firstRequestTargetingValue = FIRST_REQUEST_ARM_PREFIX + firstRequestTiming.firstRequestBucket;
        googletag.pubads().setTargeting(GPT_KEY_FIRST_REQUEST, firstRequestTargetingValue);
        adsTelemetryDebugLog(
          GPT_KEY_FIRST_REQUEST + "=" + firstRequestTargetingValue,
          "at",
          Math.round(firstRequestTiming.firstRefreshCallMs || -1),
          "ms, slots",
          firstRequestTiming.firstRefreshCallSlots,
        );

        // Note the page variant for the snapshot (debug only).
        refreshPageVariantNote();

        if (consentFinalMs !== null) {
          firstRequestTiming.consentWaitAtFirstRequest = getFirstRequestTimingBucket(consentFinalMs);
          applyConsentWaitTargeting(firstRequestTiming.consentWaitAtFirstRequest);
        } else {
          firstRequestTiming.consentWaitAtFirstRequest = CONSENT_WAIT_PENDING;
          applyConsentWaitTargeting(CONSENT_WAIT_PENDING);
          startConsentWaitResolutionPoll();
        }
        return;
      }

      // Only out-of-page / interstitial slots: no stamp, just count it.
      firstRequestTiming.skippedOutOfPageRefreshCalls = firstRequestTiming.skippedOutOfPageRefreshCalls + 1;
      adsTelemetryDebugLog("refresh of out-of-page slots only, no stamp:", describeRefreshSlots(slotList || []));
      return;
    }

    // Not the first request: only upgrade a still-pending consent value.
    if (firstRequestTiming.consentWaitBucket === CONSENT_WAIT_PENDING && consentFinalMs !== null) {
      applyConsentWaitTargeting(getFirstRequestTimingBucket(consentFinalMs));
    }
  } catch (error) {
    // Telemetry must never throw into the wrapper.
  }
}

// GPT lifecycle listeners for the debug snapshot: first request / response /
// render time per slot. Registered through the GPT queue so pubads() exists.
function installFirstRequestGptListeners() {
  try {
    googletag.cmd.push(function () {
      function slotIdOf(event) {
        try {
          return event.slot.getSlotElementId();
        } catch (error) {
          return "unknown";
        }
      }
      function recordSlotEvent(store, event) {
        try {
          var slotId = slotIdOf(event);
          if (store[slotId] === undefined) {
            store[slotId] = timingNowMilliseconds();
          }
        } catch (error) {
          // Ignore.
        }
      }
      googletag.pubads().addEventListener("slotRequested", function (event) {
        recordSlotEvent(firstRequestTiming.slotRequestedMs, event);
      });
      googletag.pubads().addEventListener("slotResponseReceived", function (event) {
        recordSlotEvent(firstRequestTiming.slotResponseReceivedMs, event);
      });
      googletag.pubads().addEventListener("slotRenderEnded", function (event) {
        recordSlotEvent(firstRequestTiming.slotRenderEndedMs, event);
      });
    });
  } catch (error) {
    // Ignore: listeners are debug-only.
  }
}

// Debug object: window.__adsTelemetry.
//   variant / variantSource -- the page's A/B variant as last read
//   readVariant()           -- re-reads the variant now and returns it
//   snapshot()              -- the raw timings behind the buckets
window.__adsTelemetry = {
  variant: null,
  variantSource: null,
  readVariant: function () {
    refreshPageVariantNote();
    return window.__adsTelemetry.variant;
  },
  snapshot: function () {
    try {
      readConsentFinalFromWatchdog();
      refreshPageVariantNote();
      var navigationTiming = null;
      try {
        var navigationEntries = window.performance.getEntriesByType("navigation");
        if (navigationEntries && navigationEntries.length > 0) {
          navigationTiming = {
            domContentLoadedEventEndMs: navigationEntries[0].domContentLoadedEventEnd,
            domInteractiveMs: navigationEntries[0].domInteractive,
            responseEndMs: navigationEntries[0].responseEnd,
          };
        }
      } catch (error) {
        navigationTiming = null;
      }
      var snapshotCopy = JSON.parse(JSON.stringify(firstRequestTiming));
      snapshotCopy.navigation = navigationTiming;
      try {
        snapshotCopy.watchdog = JSON.parse(JSON.stringify(window.__cmpWatchdog || null));
      } catch (watchdogCopyError) {
        snapshotCopy.watchdog = null;
      }
      snapshotCopy.nowMs = timingNowMilliseconds();
      return snapshotCopy;
    } catch (error) {
      return firstRequestTiming;
    }
  },
};

// Re-reads the page variant and stores it on the debug object and in the
// timing record. A known value is never overwritten by an unknown one.
function refreshPageVariantNote() {
  try {
    var variantResult = readPageVariant();
    if (variantResult.variant !== null) {
      window.__adsTelemetry.variant = variantResult.variant;
      window.__adsTelemetry.variantSource = variantResult.source;
      firstRequestTiming.pageVariant = variantResult.variant;
      firstRequestTiming.pageVariantSource = variantResult.source;
    }
  } catch (error) {
    // Debug only.
  }
}

firstRequestTiming.scriptStartMs = timingNowMilliseconds();
installFirstRequestGptListeners();
refreshPageVariantNote();
var Adhesion,
  Billboard,
  P1,
  P2,
  P3,
  P4,
  P5,
  In_Text_1,
  PREBID_TIMEOUT = 1500,
  SECONDS_TO_WAIT_AFTER_VIEWABILITY = 30,
  GAM_NOREFRESH_ADVERTISER_IDS = [],
  GAM_NOREFRESH_ORDER_IDS = [],
  GAM_NOREFRESH_ADUNITS = [],
  DISTANCE_IN_PIXELS_FOR_LAZY_LOADING = "350px",
  VISIBILITY_PERCENT_FOR_REFRESH = 0.8,
  adUnitPath = "/161634325/recepti.zena.rs/",
  adWindowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
  adUnitsATF = [],
  adUnitsBTF = [];
function runWrapper() {
  var p = document.querySelectorAll(".wrapperAd");
  function e() {
    pbjs.initAdserverSet ||
      ((pbjs.initAdserverSet = !0),
      googletag.cmd.push(function () {
        pbjs.que.push(function () {
          pbjs.getUserIdsAsync().then(function (e) {
            var d;
            try {
              (d = JSON.parse(decodeURIComponent(window.localStorage.getItem("id5id"))).gp) &&
                googletag.pubads().setPublisherProvidedId(d);
            } catch {}
            pbjs.setTargetingForGPTAsync();
            // 15102026-2 telemetry: page-level aq_first_request / aq_consent_wait
            // are set right before the first ad request of the page (passive).
            recordFirstRequestTelemetryBeforeRefresh([Adhesion, Billboard, P1, In_Text_1]);
            googletag.pubads().refresh([Adhesion, Billboard, P1, In_Text_1]);
          });
        });
      }));
  }
  (googletag.cmd.push(function () {
    var d = googletag
        .sizeMapping()
        .addSize(
          [1600, 0],
          [
            [728, 90],
            [970, 90],
            [980, 90],
            [1e3, 100],
            [750, 100],
            [750, 200],
            [1, 1],
          ],
        )
        .addSize(
          [1024, 0],
          [
            [728, 90],
            [1, 1],
          ],
        )
        .addSize(
          [469, 0],
          [
            [468, 60],
            [1, 1],
          ],
        )
        .addSize(
          [0, 0],
          [
            [300, 50],
            [320, 50],
            [300, 100],
            [320, 100],
            [1, 1],
          ],
        )
        .build(),
      i = googletag
        .sizeMapping()
        .addSize(
          [1024, 0],
          [
            [728, 90],
            [970, 66],
            [950, 90],
            [970, 90],
            [980, 90],
            [1e3, 100],
            [750, 100],
            [750, 200],
            [980, 120],
            [930, 180],
            [970, 250],
            [980, 240],
            [980, 250],
            [990, 250],
            [1060, 250],
          ],
        )
        .addSize([469, 0], [[468, 60]])
        .addSize(
          [0, 0],
          [
            [300, 50],
            [300, 75],
            [320, 80],
            [320, 50],
            [300, 100],
            [320, 100],
          ],
        )
        .build(),
      a = googletag
        .sizeMapping()
        .addSize(
          [1024, 0],
          [
            [200, 200],
            [250, 250],
            [300, 250],
            [120, 600],
            [160, 600],
            [300, 600],
          ],
        )
        .addSize(
          [469, 0],
          [
            [200, 200],
            [250, 250],
            [300, 250],
          ],
        )
        .addSize(
          [0, 0],
          [
            [200, 200],
            [250, 250],
            [300, 250],
            [336, 280],
          ],
        )
        .build(),
      r = googletag
        .sizeMapping()
        .addSize(
          [1024, 0],
          [
            [200, 200],
            [250, 250],
            [300, 250],
            [120, 600],
            [160, 600],
            [300, 600],
          ],
        )
        .addSize(
          [469, 0],
          [
            [200, 200],
            [250, 250],
            [300, 250],
          ],
        )
        .addSize(
          [0, 0],
          [
            [200, 200],
            [250, 250],
            [300, 250],
            [336, 280],
          ],
        )
        .build();
    ("undefined" != typeof adsKeywords &&
      void 0 !== adsKeywords.pageType &&
      "home" === adsKeywords.pageType &&
      (r = googletag
        .sizeMapping()
        .addSize(
          [1024, 0],
          [
            [728, 90],
            [970, 66],
            [950, 90],
            [970, 90],
            [750, 100],
            [750, 200],
            [980, 120],
            [930, 180],
            [970, 250],
            [300, 250],
          ],
        )
        .addSize(
          [469, 0],
          [
            [200, 200],
            [250, 250],
            [300, 250],
          ],
        )
        .addSize(
          [0, 0],
          [
            [200, 200],
            [250, 250],
            [300, 250],
            [336, 280],
          ],
        )
        .build()),
      "undefined" != typeof adsKeywords &&
        void 0 !== adsKeywords.pageType &&
        "article" === adsKeywords.pageType &&
        (r = googletag
          .sizeMapping()
          .addSize(
            [1024, 0],
            [
              [200, 200],
              [250, 250],
              [300, 250],
            ],
          )
          .addSize(
            [469, 0],
            [
              [200, 200],
              [250, 250],
              [300, 250],
            ],
          )
          .addSize(
            [0, 0],
            [
              [200, 200],
              [250, 250],
              [300, 250],
              [336, 280],
            ],
          )
          .build()));
    var t = googletag
      .sizeMapping()
      .addSize([1024, 0], [[580, 280], [200, 200], [250, 250], [300, 250], [320, 250], [320, 240], [336, 280], "fluid"])
      .addSize(
        [469, 0],
        [
          [300, 50],
          [320, 50],
          [468, 60],
          [200, 200],
          [250, 250],
          [300, 250],
          [320, 250],
          [320, 240],
          [336, 280],
          "fluid",
        ],
      )
      .addSize(
        [0, 0],
        [
          [300, 50],
          [300, 75],
          [320, 80],
          [320, 50],
          [300, 100],
          [320, 100],
          [200, 200],
          [250, 250],
          [300, 250],
          [320, 250],
          [320, 240],
          [336, 280],
          "fluid",
        ],
      )
      .build();
    (p.forEach(function (e) {
      "Adhesion" === e.id
        ? ((Adhesion = googletag
            .defineSlot(
              adUnitPath + "Sticky",
              [
                [728, 90],
                [970, 90],
                [320, 50],
              ],
              "Adhesion",
            )
            .defineSizeMapping(d)
            .setTargeting("position", "Adhesion")
            .setTargeting("refresh_count", "0")
            .addService(googletag.pubads())),
          adUnitsATF.push({
            code: "Adhesion",
            mediaTypes: {
              banner: {
                sizeConfig: [
                  {
                    minViewPort: [1600, 0],
                    sizes: [
                      [728, 90],
                      [970, 90],
                      [750, 100],
                      [750, 200],
                    ],
                  },
                  { minViewPort: [1024, 0], sizes: [728, 90] },
                  { minViewPort: [469, 0], sizes: [[468, 60]] },
                  {
                    minViewPort: [0, 0],
                    sizes: [
                      [300, 50],
                      [320, 50],
                      [300, 100],
                      [320, 100],
                    ],
                  },
                ],
              },
            },
            bids: [
              { bidder: "adform", params: { mid: "1705626", priceType: "net" } },
              // 15102026-2: ConnectAd added (site-level ids from the Header Bidding IDs sheet).
              { bidder: "connectad", params: { networkId: 44, siteId: 1084677 } },
              { bidder: "eskimi", params: { placementId: 1573 } },
              { bidder: "ix", params: { siteId: "992510" } },
              { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559516790" } },
              // 15102026-2: PubMatic (publisher 158892, per-unit ad slot) and Rich Audience (per-unit pid) added.
              { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5216343" } },
              { bidder: "richaudience", params: { pid: "KGG1SrOw36", supplyType: "site" } },
              {
                bidder: "rtbhouse",
                params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.1 },
              },
              { bidder: "rubicon", params: { accountId: 19712, siteId: 491192, zoneId: 2914468 } },
              {
                bidder: "smartadserver",
                params: { networkId: "4527", siteId: "600146", pageId: "1834193", formatId: "120620" },
              },
              { bidder: "teads", params: { pageId: 182422, placementId: 197427 } },
            ],
          }))
        : "Billboard" === e.id
          ? ((Billboard = googletag
              .defineSlot(adUnitPath + "Billboard", [[970, 250]], "Billboard")
              .defineSizeMapping(i)
              .setTargeting("position", "Billboard")
              .setTargeting("refresh_count", "0")
              .addService(googletag.pubads())),
            adUnitsATF.push({
              code: "Billboard",
              mediaTypes: {
                banner: {
                  sizeConfig: [
                    {
                      minViewPort: [1024, 0],
                      sizes: [
                        [970, 90],
                        [728, 90],
                        [970, 250],
                      ],
                    },
                    { minViewPort: [469, 0], sizes: [[468, 60]] },
                    {
                      minViewPort: [0, 0],
                      sizes: [
                        [300, 50],
                        [320, 50],
                        [300, 100],
                        [320, 100],
                      ],
                    },
                  ],
                },
              },
              bids: [
                { bidder: "adform", params: { mid: "1705627", priceType: "net" } },
                // 15102026-2: ConnectAd added (site-level ids from the Header Bidding IDs sheet).
                { bidder: "connectad", params: { networkId: 44, siteId: 1084677 } },
                { bidder: "eskimi", params: { placementId: 1573 } },
                { bidder: "ix", params: { siteId: "992511" } },
                { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559516791" } },
                // 15102026-2: PubMatic (publisher 158892, per-unit ad slot) and Rich Audience (per-unit pid) added.
                { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5216344" } },
                { bidder: "richaudience", params: { pid: "lEgLmRxVmn", supplyType: "site" } },
                {
                  bidder: "rtbhouse",
                  params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.1 },
                },
                { bidder: "rubicon", params: { accountId: 19712, siteId: 491192, zoneId: 2914470 } },
                {
                  bidder: "smartadserver",
                  params: { networkId: "4527", siteId: "600146", pageId: "1834193", formatId: "120621" },
                },
                { bidder: "teads", params: { pageId: 182422, placementId: 197427 } },
              ],
            }))
          : "P1" === e.id
            ? ((P1 = googletag
                .defineSlot(adUnitPath + "P1", [[300, 250]], "P1")
                .defineSizeMapping(a)
                .setTargeting("position", "P1")
                .setTargeting("refresh_count", "0")
                .addService(googletag.pubads())),
              adUnitsATF.push({
                code: "P1",
                mediaTypes: {
                  banner: {
                    sizeConfig: [
                      {
                        minViewPort: [1024, 0],
                        sizes: [
                          [200, 200],
                          [250, 250],
                          [300, 250],
                          [120, 600],
                          [160, 600],
                          [300, 600],
                        ],
                      },
                      {
                        minViewPort: [469, 0],
                        sizes: [
                          [200, 200],
                          [250, 250],
                          [300, 250],
                        ],
                      },
                      {
                        minViewPort: [0, 0],
                        sizes: [
                          [200, 200],
                          [250, 250],
                          [300, 250],
                          [336, 280],
                        ],
                      },
                    ],
                  },
                },
                bids: [
                  { bidder: "adform", params: { mid: "1705628", priceType: "net" } },
                  // 15102026-2: ConnectAd added (site-level ids from the Header Bidding IDs sheet).
                  { bidder: "connectad", params: { networkId: 44, siteId: 1084677 } },
                  { bidder: "eskimi", params: { placementId: 1573 } },
                  { bidder: "ix", params: { siteId: "992512" } },
                  { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559516792" } },
                  // 15102026-2: PubMatic (publisher 158892, per-unit ad slot) and Rich Audience (per-unit pid) added.
                  { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5216345" } },
                  { bidder: "richaudience", params: { pid: "7USJA5dfK5", supplyType: "site" } },
                  {
                    bidder: "rtbhouse",
                    params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.1 },
                  },
                  { bidder: "rubicon", params: { accountId: 19712, siteId: 491192, zoneId: 2914472 } },
                  {
                    bidder: "smartadserver",
                    params: { networkId: "4527", siteId: "600146", pageId: "1834193", formatId: "120624" },
                  },
                  { bidder: "teads", params: { pageId: 182422, placementId: 197427 } },
                ],
              }))
            : "P2" === e.id
              ? ((P2 = googletag
                  .defineSlot(adUnitPath + "P2", [[300, 250]], "P2")
                  .defineSizeMapping(r)
                  .setTargeting("position", "P2")
                  .setTargeting("refresh_count", "0")
                  .addService(googletag.pubads())),
                adUnitsBTF.push({
                  code: "P2",
                  mediaTypes: {
                    banner: {
                      sizeConfig: [
                        {
                          minViewPort: [1024, 0],
                          sizes: [
                            [200, 200],
                            [250, 250],
                            [300, 250],
                          ],
                        },
                        {
                          minViewPort: [469, 0],
                          sizes: [
                            [200, 200],
                            [250, 250],
                            [300, 250],
                          ],
                        },
                        {
                          minViewPort: [0, 0],
                          sizes: [
                            [200, 200],
                            [250, 250],
                            [300, 250],
                            [336, 280],
                          ],
                        },
                      ],
                    },
                  },
                  bids: [
                    { bidder: "adform", params: { mid: "1705629", priceType: "net" } },
                    // 15102026-2: ConnectAd added (site-level ids from the Header Bidding IDs sheet).
                    { bidder: "connectad", params: { networkId: 44, siteId: 1084677 } },
                    { bidder: "eskimi", params: { placementId: 1573 } },
                    { bidder: "ix", params: { siteId: "992513" } },
                    { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559516793" } },
                    // 15102026-2: PubMatic (publisher 158892, per-unit ad slot) and Rich Audience (per-unit pid) added.
                    { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5216346" } },
                    { bidder: "richaudience", params: { pid: "HDae3XLNP1", supplyType: "site" } },
                    {
                      bidder: "rtbhouse",
                      params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.1 },
                    },
                    { bidder: "rubicon", params: { accountId: 19712, siteId: 491192, zoneId: 2914474 } },
                    {
                      bidder: "smartadserver",
                      params: { networkId: "4527", siteId: "600146", pageId: "1834193", formatId: "120625" },
                    },
                    { bidder: "teads", params: { pageId: 182422, placementId: 197427 } },
                  ],
                }))
              : "P3" === e.id
                ? ((P3 = googletag
                    .defineSlot(adUnitPath + "P3", [[300, 250]], "P3")
                    .defineSizeMapping(r)
                    .setTargeting("position", "P3")
                    .setTargeting("refresh_count", "0")
                    .addService(googletag.pubads())),
                  adUnitsBTF.push({
                    code: "P3",
                    mediaTypes: {
                      banner: {
                        sizeConfig: [
                          {
                            minViewPort: [1024, 0],
                            sizes: [
                              [200, 200],
                              [250, 250],
                              [300, 250],
                            ],
                          },
                          {
                            minViewPort: [469, 0],
                            sizes: [
                              [200, 200],
                              [250, 250],
                              [300, 250],
                            ],
                          },
                          {
                            minViewPort: [0, 0],
                            sizes: [
                              [200, 200],
                              [250, 250],
                              [300, 250],
                              [336, 280],
                            ],
                          },
                        ],
                      },
                    },
                    bids: [
                      { bidder: "adform", params: { mid: "1753062", priceType: "net" } },
                      // 15102026-2: ConnectAd added (site-level ids from the Header Bidding IDs sheet).
                      { bidder: "connectad", params: { networkId: 44, siteId: 1084677 } },
                      { bidder: "eskimi", params: { placementId: 1573 } },
                      { bidder: "ix", params: { siteId: "1033454" } },
                      { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559748177" } },
                      // 15102026-2: PubMatic (publisher 158892, per-unit ad slot) and Rich Audience (per-unit pid) added.
                      { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5383721" } },
                      { bidder: "richaudience", params: { pid: "bhrLHFs1cO", supplyType: "site" } },
                      {
                        bidder: "rtbhouse",
                        params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.1 },
                      },
                      { bidder: "rubicon", params: { accountId: 19712, siteId: 491192, zoneId: 3035236 } },
                      {
                        bidder: "smartadserver",
                        params: { networkId: "4527", siteId: "600146", pageId: "1834193", formatId: "120626" },
                      },
                      { bidder: "teads", params: { pageId: 182422, placementId: 197427 } },
                    ],
                  }))
                : "P4" === e.id
                  ? ((P4 = googletag
                      .defineSlot(adUnitPath + "P4", [[300, 250]], "P4")
                      .defineSizeMapping(r)
                      .setTargeting("position", "P4")
                      .setTargeting("refresh_count", "0")
                      .addService(googletag.pubads())),
                    adUnitsBTF.push({
                      code: "P4",
                      mediaTypes: {
                        banner: {
                          sizeConfig: [
                            {
                              minViewPort: [1024, 0],
                              sizes: [
                                [200, 200],
                                [250, 250],
                                [300, 250],
                              ],
                            },
                            {
                              minViewPort: [469, 0],
                              sizes: [
                                [200, 200],
                                [250, 250],
                                [300, 250],
                              ],
                            },
                            {
                              minViewPort: [0, 0],
                              sizes: [
                                [200, 200],
                                [250, 250],
                                [300, 250],
                                [336, 280],
                              ],
                            },
                          ],
                        },
                      },
                      bids: [
                        { bidder: "adform", params: { mid: "1753063", priceType: "net" } },
                        // 15102026-2: ConnectAd added (site-level ids from the Header Bidding IDs sheet).
                        { bidder: "connectad", params: { networkId: 44, siteId: 1084677 } },
                        { bidder: "eskimi", params: { placementId: 1573 } },
                        { bidder: "ix", params: { siteId: "1033455" } },
                        { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559748178" } },
                        // 15102026-2: PubMatic (publisher 158892, per-unit ad slot) and Rich Audience (per-unit pid) added.
                        { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5383722" } },
                        { bidder: "richaudience", params: { pid: "tL5dO8Glnk", supplyType: "site" } },
                        {
                          bidder: "rtbhouse",
                          params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.1 },
                        },
                        { bidder: "rubicon", params: { accountId: 19712, siteId: 491192, zoneId: 3035238 } },
                        {
                          bidder: "smartadserver",
                          params: { networkId: "4527", siteId: "600146", pageId: "1834193", formatId: "123584" },
                        },
                        { bidder: "teads", params: { pageId: 182422, placementId: 197427 } },
                      ],
                    }))
                  : "P5" === e.id
                    ? ((P5 = googletag
                        .defineSlot(adUnitPath + "P5", [[300, 250]], "P5")
                        .defineSizeMapping(r)
                        .setTargeting("position", "P5")
                        .setTargeting("refresh_count", "0")
                        .addService(googletag.pubads())),
                      adUnitsBTF.push({
                        code: "P5",
                        mediaTypes: {
                          banner: {
                            sizeConfig: [
                              {
                                minViewPort: [1024, 0],
                                sizes: [
                                  [200, 200],
                                  [250, 250],
                                  [300, 250],
                                ],
                              },
                              {
                                minViewPort: [469, 0],
                                sizes: [
                                  [200, 200],
                                  [250, 250],
                                  [300, 250],
                                ],
                              },
                              {
                                minViewPort: [0, 0],
                                sizes: [
                                  [200, 200],
                                  [250, 250],
                                  [300, 250],
                                  [336, 280],
                                ],
                              },
                            ],
                          },
                        },
                        bids: [
                          // 15102026-2: P5 Adform mid corrected 1705630 -> 1753064 (1705630 is the In_Text_1 placement;
                          // the Header Bidding IDs sheet lists 1753064 for P5).
                          { bidder: "adform", params: { mid: "1753064", priceType: "net" } },
                          // 15102026-2: ConnectAd added (site-level ids from the Header Bidding IDs sheet).
                          { bidder: "connectad", params: { networkId: 44, siteId: 1084677 } },
                          { bidder: "eskimi", params: { placementId: 1573 } },
                          { bidder: "ix", params: { siteId: "1033456" } },
                          { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559748179" } },
                          // 15102026-2: PubMatic (publisher 158892, per-unit ad slot) and Rich Audience (per-unit pid) added.
                          { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5383723" } },
                          { bidder: "richaudience", params: { pid: "LZSnWnJMUi", supplyType: "site" } },
                          {
                            bidder: "rtbhouse",
                            params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.1 },
                          },
                          { bidder: "rubicon", params: { accountId: 19712, siteId: 491192, zoneId: 3035240 } },
                          {
                            bidder: "smartadserver",
                            params: { networkId: "4527", siteId: "600146", pageId: "1834193", formatId: "125177" },
                          },
                          { bidder: "teads", params: { pageId: 182422, placementId: 197427 } },
                        ],
                      }))
                    : "In_Text_1" === e.id &&
                      ((In_Text_1 = googletag
                        .defineSlot(adUnitPath + "In_Text_1", [[300, 250]], "In_Text_1")
                        .defineSizeMapping(t)
                        .setTargeting("position", "In_Text_1")
                        .setTargeting("refresh_count", "0")
                        .addService(googletag.pubads())),
                      adUnitsATF.push({
                        code: "In_Text_1",
                        mediaTypes: {
                          banner: {
                            sizeConfig: [
                              {
                                minViewPort: [1024, 0],
                                sizes: [
                                  [200, 200],
                                  [250, 250],
                                  [300, 250],
                                  [336, 280],
                                ],
                              },
                              {
                                minViewPort: [469, 0],
                                sizes: [
                                  [468, 60],
                                  [200, 200],
                                  [250, 250],
                                  [300, 250],
                                  [336, 280],
                                  [300, 50],
                                  [320, 50],
                                ],
                              },
                              {
                                minViewPort: [0, 0],
                                sizes: [
                                  [300, 50],
                                  [320, 50],
                                  [300, 100],
                                  [320, 100],
                                  [200, 200],
                                  [250, 250],
                                  [300, 250],
                                  [336, 280],
                                ],
                              },
                            ],
                          },
                        },
                        bids: [
                          { bidder: "adform", params: { mid: "1705630", priceType: "net" } },
                          // 15102026-2: ConnectAd added (site-level ids from the Header Bidding IDs sheet).
                          { bidder: "connectad", params: { networkId: 44, siteId: 1084677 } },
                          { bidder: "eskimi", params: { placementId: 1573 } },
                          { bidder: "ix", params: { siteId: "992514" } },
                          { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559516794" } },
                          // 15102026-2: PubMatic (publisher 158892, per-unit ad slot) and Rich Audience (per-unit pid) added.
                          { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5216347" } },
                          { bidder: "richaudience", params: { pid: "xdoUoWp1EN", supplyType: "site" } },
                          {
                            bidder: "rtbhouse",
                            params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.1 },
                          },
                          { bidder: "rubicon", params: { accountId: 19712, siteId: 491192, zoneId: 2914476 } },
                          {
                            bidder: "smartadserver",
                            params: { networkId: "4527", siteId: "600146", pageId: "1834193", formatId: "120627" },
                          },
                          { bidder: "teads", params: { pageId: 182423, placementId: 197428 } },
                        ],
                      }));
    }),
      googletag.pubads().addEventListener("slotRenderEnded", function (e) {
        var d = e.slot.getAdUnitPath(),
          i = document.getElementById("adhesion_background");
        if (d === adUnitPath + "Sticky" && !0 === e.isEmpty) {
          let e = document.getElementById("Adhesion");
          e.style.display = "none";
        } else if (d === adUnitPath + "Sticky" && null === i) {
          let d = document.getElementById("Adhesion");
          ((d.style.position = "fixed"),
            (d.style.zIndex = "999999"),
            (d.style.width = "100%"),
            (d.style.height = ""),
            (d.style.bottom = "0px"),
            (d.style.textAlign = "center"),
            (d.style.padding = "10px 0px 0px"),
            (d.style.borderTop = "1px #CCC solid"));
          let e = document.createElement("div");
          (e.setAttribute("ID", "adhesion_background"),
            (e.style.backgroundColor = "#FFF"),
            (e.style.opacity = 1024 < adWindowWidth ? "70%" : "100%"),
            (e.style.position = "absolute"),
            (e.style.height = "100%"),
            (e.style.width = "100%"),
            (e.style.top = "0"),
            (e.style.left = "0"),
            (e.style.zIndex = "-1"),
            d.appendChild(e));
          let i = document.createElement("div");
          (i.setAttribute("ID", "close_adhesion"),
            (i.style.position = "absolute"),
            (i.style.top = "-29px"),
            (i.style.right = "0px"),
            (i.style.cursor = "pointer"),
            (i.style.padding = "8px"),
            (i.style.border = "1px solid #CCC"),
            (i.style.background = "#FFF"),
            (i.style.borderBottom = "1px solid #FFF"),
            (i.style.borderTopLeftRadius = "10px"));
          let a = document.createElementNS("http://www.w3.org/2000/svg", "svg"),
            r = document.createElementNS("http://www.w3.org/2000/svg", "path");
          (a.setAttribute("display", "block"),
            a.setAttribute("fill", "#000"),
            a.setAttribute("width", "12px"),
            a.setAttribute("height", "12px"),
            a.setAttribute("viewBox", "0 0 8 8"),
            r.setAttribute(
              "d",
              "M1.41 0l-1.41 1.41.72.72 1.78 1.81-1.78 1.78-.72.69 1.41 1.44.72-.72 1.81-1.81 1.78 1.81.69.72 1.44-1.44-.72-.69-1.81-1.78 1.81-1.81.72-.72-1.44-1.41-.69.72-1.78 1.78-1.81-1.78-.72-.72z",
            ),
            a.appendChild(r),
            i.appendChild(a),
            d.appendChild(i),
            document.getElementById("close_adhesion").addEventListener(
              "click",
              function (e) {
                (e.preventDefault(), d.parentNode.removeChild(d));
              },
              !1,
            ));
        }
      }),
      "undefined" != typeof adsKeywords &&
        (void 0 !== adsKeywords.category && googletag.pubads().setTargeting("category", adsKeywords.category),
        void 0 !== adsKeywords.pageType && googletag.pubads().setTargeting("pageType", adsKeywords.pageType),
        void 0 !== adsKeywords.articleUuid &&
          googletag.pubads().setTargeting("articleId", [adsKeywords.articleUuid].join(",")),
        void 0 !== adsKeywords.articleTags &&
          googletag.pubads().setTargeting("articleTags", [adsKeywords.articleTags].join(","))));
    var e,
      s = window.location.search;
    let n = new URLSearchParams(s);
    (n.has("adTest") && ((s = n.get("adTest")), googletag.pubads().setTargeting("adTest", s)),
      n.has("utm_source") && ((e = n.get("utm_source")), googletag.pubads().setTargeting("utmSource", e)),
      n.has("utm_medium") && ((e = n.get("utm_medium")), googletag.pubads().setTargeting("utmMedium", e)));
    let o = document.referrer;
    (0 === o.length
      ? googletag.pubads().setTargeting("referrer", "direct")
      : o.includes("google")
        ? googletag.pubads().setTargeting("referrer", "google")
        : o.includes("linkedin") || o.includes("t.com") || o.includes("facebook") || o.includes("instagram")
          ? googletag.pubads().setTargeting("referrer", "social")
          : o.includes("blic.rs") ||
              o.includes("pulsonline.rs") ||
              o.includes("ana.rs") ||
              o.includes("nekretnine.rs") ||
              o.includes("mojauto.rs")
            ? googletag.pubads().setTargeting("referrer", "ringier")
            : googletag.pubads().setTargeting("referrer", "other"),
      googletag.pubads().disableInitialLoad(),
      googletag.pubads().enableSingleRequest(),
      googletag.pubads().setCentering(!0),
      googletag.enableServices(),
      googletag
        .pubads()
        .getSlots()
        .forEach(function (e) {
          googletag.display(e.getSlotElementId());
        }));
  }),
    pbjs.que.push(function () {
      (
        pbjs.setConfig({
          priceGranularity: "high",
          enableSendAllBids: !1,
          targetingControls: { alwaysIncludeDeals: !0 },
          currency: {
            adServerCurrency: "EUR",
            conversionRateFile: "https://cdn.jsdelivr.net/gh/prebid/currency-file@1/latest.json",
          },
          userSync: {
            syncEnabled: !0,
            syncsPerBidder: 5,
            syncDelay: 3e3,
            auctionDelay: 50,
            userIds: [
              { name: "sharedId", storage: { type: "cookie", name: "_sharedid", expires: 365 } },
              {
                name: "id5Id",
                params: { partner: 1355, externalModuleUrl: "https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js" },
                storage: { type: "html5", name: "id5id", expires: 90, refreshInSeconds: 28800 },
              },
              { name: "teadsId", params: { pubId: 26849 } },
              // 15102026-2: the { name: "criteo" } user id was removed together with the Criteo bidder;
              // criteoIdSystem is no longer in the Prebid library.
              { name: "lotamePanoramaId" },
            ],
            aliasSyncEnabled: !0,
            filterSettings: { all: { bidders: "*", filter: "include" } },
          },
          enableTIDs: !0,
          consentManagement: { gdpr: { cmpApi: "iab", timeout: 8e3, defaultGdprScope: !0 } },
        }),
        (pbjs.bidderSettings = { standard: { storageAllowed: !0 } }),
        pbjs.addAdUnits(adUnitsATF),
        // 15102026-2 telemetry: note when the first (ATF) auction starts (debug snapshot only).
        recordFirstRequestMilestone("firstRequestBidsMs"),
        pbjs.requestBids({ bidsBackHandler: e, timeout: PREBID_TIMEOUT }));
    }),
    pbjs.que.push(function () {
      pbjs.addAdUnits(adUnitsBTF);
    }));
  var i = new IntersectionObserver(
    function (e) {
      e.forEach((e) => {
        var d;
        e.isIntersecting &&
          ((d = e.target.id),
          pbjs.que.push(function () {
            pbjs.requestBids({
              timeout: PREBID_TIMEOUT,
              adUnitCodes: [d],
              bidsBackHandler: function () {
                (pbjs.setTargetingForGPTAsync([d]),
                  // 15102026-2 telemetry: stamp / consent upgrade right before the lazy request (passive).
                  recordFirstRequestTelemetryBeforeRefresh([findGptSlotByElementId(d)]),
                  "P2" === d
                    ? googletag.pubads().refresh([P2])
                    : "P3" === d
                      ? googletag.pubads().refresh([P3])
                      : "P4" === d
                        ? googletag.pubads().refresh([P4])
                        : "P5" === d && googletag.pubads().refresh([P5]));
              },
            });
          }),
          i.unobserve(e.target));
      });
    },
    { rootMargin: DISTANCE_IN_PIXELS_FOR_LAZY_LOADING },
  );
  (document.querySelectorAll(".lazyAd").forEach((e) => {
    i.observe(e);
  }),
    googletag
      .pubads()
      .getSlots()
      .forEach(function (i) {
        var a = i.getSlotElementId();
        let r = i.getTargeting("refresh_count"),
          t;
        var d = new IntersectionObserver(
          function (e) {
            e.forEach((e) => {
              var d = e.target.id;
              e.isIntersecting
                ? (t = setInterval(function () {
                    let e;
                    try {
                      e = i.getResponseInformation().advertiserId;
                    } catch (e) {}
                    GAM_NOREFRESH_ADVERTISER_IDS.includes(e) ||
                      GAM_NOREFRESH_ADUNITS.includes(a) ||
                      "visible" !== document.visibilityState ||
                      pbjs.requestBids({
                        timeout: PREBID_TIMEOUT,
                        adUnitCodes: [d],
                        bidsBackHandler: function () {
                          (pbjs.setTargetingForGPTAsync([d]),
                            // 15102026-2 telemetry: consent upgrade right before the refresh (passive).
                            recordFirstRequestTelemetryBeforeRefresh([i]),
                            googletag.pubads().refresh([i]),
                            r++,
                            i.setTargeting("refresh_count", r));
                        },
                      });
                  }, 1e3 * SECONDS_TO_WAIT_AFTER_VIEWABILITY))
                : clearInterval(t);
            });
          },
          { threshold: VISIBILITY_PERCENT_FOR_REFRESH },
        );
        document.querySelectorAll("#" + a).forEach((e) => {
          d.observe(e);
        });
      }));
}
